/**
 * Financials Domain
 * Types, store, and handlers for financial reports.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { handleTeamResourcesUpdate } from './teams';
import { handleSessionTicks } from './match-session-script';
import type { FinancialReport } from '../constants/op-codes';
import { getCurrentPlayer, getCurrentTeam } from './current-team';

// ==================== TYPES ====================

interface FinancialNotification {
  report: FinancialReport;
  newCash: number;
  timestamp: number;
  noChanges?: boolean; // Flag to indicate "no changes" notification
  isQuarterBoundary?: boolean; // True when this tick completes a quarter
}

// ==================== STORE ====================

/** An update the server finished: its number, when it began (Unix seconds) and how long it took. */
interface TickProcessed {
  gameTick: number;
  startedAtUnix: number;
  durationMs: number;
}

/** What the server says of an update: begun, finished (with its duration), or under way. */
interface TickProcessingUpdate {
  isProcessing: boolean;
  startedAtUnix?: number;
  gameTick?: number;
  durationMs?: number;
}

interface FinancialsState {
  /** Current notification to display (null if none) */
  currentNotification: FinancialNotification | null;

  /** History of all financial reports for the player's team */
  history: FinancialReport[];

  /** Profit values per financial tick (includes no-change ticks) */
  profitTicks: number[];

  /** Unix timestamp of when the next report will be generated */
  nextReportAt: number | null;

  /** Interval in seconds between reports */
  intervalSeconds: number | null;

  /** Current team tick */
  currentTeamTick: number | null;

  /** Whether innovation (KP) spending on the tech tree is locked (admin toggle) */
  innovationSpendLocked: boolean;

  /** True while the server is executing a simulation tick */
  isTickProcessing: boolean;

  /** The update under way: when it began (Unix seconds), while it runs. */
  tickProcessingStartedAtUnix: number | null;

  /** The update under way's number, when the server said: the timer sync on joining does not. */
  tickProcessingGameTick: number | null;

  /** The last update to finish since the player joined: its number, when it began and how long it took. */
  lastTickProcessed: TickProcessed | null;

  /** Show a new financial notification */
  showNotification: (report: FinancialReport, newCash: number, isQuarterBoundary?: boolean) => void;

  /** Show a "no changes" notification */
  showNoChangesNotification: () => void;

  /** Add a report to history */
  addToHistory: (report: FinancialReport) => void;

  /** Set the timer information */
  setTimerInfo: (nextReportAt: number, intervalSeconds: number) => void;

  /** Set the current team tick */
  setCurrentTeamTick: (tick: number) => void;

  /** Set whether innovation spending is locked */
  setInnovationSpendLocked: (locked: boolean) => void;

  /** Set global tick processing state: an update begun, finished, or under way as the player joined */
  setTickProcessingState: (update: TickProcessingUpdate) => void;

  /** Reset the store */
  reset: () => void;
}

const initialState = {
  currentNotification: null as FinancialNotification | null,
  history: [] as FinancialReport[],
  profitTicks: [] as number[],
  nextReportAt: null as number | null,
  intervalSeconds: null as number | null,
  currentTeamTick: null as number | null,
  innovationSpendLocked: false,
  isTickProcessing: false,
  tickProcessingStartedAtUnix: null as number | null,
  tickProcessingGameTick: null as number | null,
  lastTickProcessed: null as TickProcessed | null,
};

export const useFinancialsStore = create<FinancialsState>()(
  devtools(
    (set) => ({
      ...initialState,

      showNotification: (report, newCash, isQuarterBoundary) => {
        console.log('[FinancialsStore] Showing notification:', report);
        set(
          (state) => ({
            currentNotification: {
              report,
              newCash,
              timestamp: Date.now(),
              noChanges: false,
              isQuarterBoundary: isQuarterBoundary ?? false,
            },
            profitTicks: [...state.profitTicks, report.profit].slice(-50),
          }),
          false,
          'showNotification'
        );
      },

      showNoChangesNotification: () => {
        console.log('[FinancialsStore] Showing no changes notification');
        set(
          (state) => {
            const lastKnownProfit = state.profitTicks.at(-1) ?? state.history.at(-1)?.profit ?? 0;

            // Create a dummy report with zeros (tooltip uses history for display when `noChanges`).
            const dummyReport: FinancialReport = {
              team_id: 0,
              spending: 0,
              revenue: 0,
              cargo_revenue: 0,
              profit: 0,
              tick: 0,
              km_flown: 0,
              passengers: 0,
              passenger_km: 0,
              influence_earned: 0,
            };

            return {
              currentNotification: {
                report: dummyReport,
                newCash: 0,
                timestamp: Date.now(),
                noChanges: true,
              },
              profitTicks: [...state.profitTicks, lastKnownProfit].slice(-50),
            };
          },
          false,
          'showNoChangesNotification'
        );
      },

      addToHistory: (report) => {
        set(
          (state) => ({
            history: [...state.history, report],
          }),
          false,
          'addToHistory'
        );
      },

      setTimerInfo: (nextReportAt, intervalSeconds) => {
        set(
          // next_report_at == 0 means the server has no active sim schedule (StartLobby / EndLobby).
          // Store null so the client shows "--:--" instead of a phantom countdown.
          { nextReportAt: nextReportAt > 0 ? nextReportAt : null, intervalSeconds },
          false,
          'setTimerInfo'
        );
      },

      setCurrentTeamTick: (tick) => {
        set(
          { currentTeamTick: tick },
          false,
          'setCurrentTeamTick'
        );
      },

      setInnovationSpendLocked: (locked) => {
        set(
          { innovationSpendLocked: locked },
          false,
          'setInnovationSpendLocked'
        );
      },

      setTickProcessingState: ({ isProcessing, startedAtUnix, gameTick, durationMs }) => {
        set(
          (state) => ({
            isTickProcessing: isProcessing,
            tickProcessingStartedAtUnix: isProcessing && startedAtUnix ? startedAtUnix : null,
            tickProcessingGameTick: isProcessing ? (gameTick ?? null) : null,
            // A finished update with its figures; one that only says it is
            // over keeps the last one known.
            lastTickProcessed:
              !isProcessing && startedAtUnix && durationMs !== undefined && gameTick !== undefined
                ? { gameTick, startedAtUnix, durationMs }
                : state.lastTickProcessed,
          }),
          false,
          'setTickProcessingState',
        );
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    { name: 'financials-store' }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle OpCodeFinancialReport message
 * Shows a popup notification and updates team cash
 * The message contains reports for all teams - we check if our team has one
 */
export function handleFinancialReport(data: {
  reports: { [team_id: number]: FinancialReport };
  teams: { [team_id: number]: { team_id: number; new_cash: number; new_influence: number; lifetime_passengers?: number; lifetime_passenger_miles?: number; influence_seconds?: number; current_tick?: number; is_quarter_boundary?: boolean } };
  next_report_at: number;
  interval_seconds: number;
  game_tick?: number;
  session_ticks?: number;
}) {
  console.log('[FinancialsHandler] Received financial reports:', data);

  const store = useFinancialsStore.getState();

  // Update timer info (always, regardless of whether our team has a report)
  store.setTimerInfo(data.next_report_at, data.interval_seconds);

  // Update session ticks so the session-relative quarter display stays current.
  if (data.session_ticks !== undefined) {
    handleSessionTicks(data.session_ticks);
  }

  // Get current player's team
  const teamId = getCurrentTeam()?.team_id ?? getCurrentPlayer()?.team_id;

  if (!teamId) {
    console.log('[FinancialsHandler] No team found for current player');
    return;
  }

  const report = data.reports[teamId];
  const teamData = data.teams[teamId];

  // Update current team tick
  if (teamData?.current_tick !== undefined) {
    store.setCurrentTeamTick(teamData.current_tick);
  } else if (report?.tick !== undefined) {
    store.setCurrentTeamTick(report.tick);
  }

  if (report && teamData) {
    // Our team has financial activity - show the report
    console.log('[FinancialsHandler] Our team has activity, showing report');

    // Add to history
    store.addToHistory(report);

    // Show notification popup with quarter boundary flag
    const isQuarterBoundary = teamData.is_quarter_boundary ?? false;
    store.showNotification(report, teamData.new_cash, isQuarterBoundary);

    // Update team's cash, influence, and lifetime passengers in teams store.
    // Knowledge Points are now per-player and travel with OnlinePlayerData (see usePlayersStore).
    handleTeamResourcesUpdate(teamId, teamData.new_cash, teamData.new_influence, teamData.lifetime_passengers, teamData.lifetime_passenger_miles, teamData.influence_seconds);
  } else {
    // Our team has no financial activity - show "no activity" notification
    console.log('[FinancialsHandler] Our team has no activity, showing no-activity notification');
    store.showNoChangesNotification();
  }
}

/**
 * Handle OpCodeTimerSync message (for initial join)
 * Updates timer information when player first joins
 */
export function handleTimerSync(data: {
  next_report_at: number;
  interval_seconds: number;
  current_team_tick?: number;
  game_tick?: number;
  session_ticks?: number;
  innovation_spend_locked?: boolean;
  is_tick_processing?: boolean;
  tick_processing_started_at_unix?: number;
}) {
  console.log('[FinancialsHandler] Received timer sync (initial join):', data);

  const store = useFinancialsStore.getState();
  store.setTimerInfo(data.next_report_at, data.interval_seconds);

  // Update current team tick if provided
  if (data.current_team_tick !== undefined) {
    store.setCurrentTeamTick(data.current_team_tick);
  }

  // Update session ticks for session-relative quarter display.
  if (data.session_ticks !== undefined) {
    handleSessionTicks(data.session_ticks);
  }

  if (data.innovation_spend_locked !== undefined) {
    store.setInnovationSpendLocked(data.innovation_spend_locked);
  }

  if (data.is_tick_processing !== undefined) {
    // Under way as the player joins: when it began, but not its number.
    store.setTickProcessingState({
      isProcessing: data.is_tick_processing,
      startedAtUnix: data.tick_processing_started_at_unix,
    });
  }
}

/**
 * Handle OpCodeTickProcessingState message
 * Sets whether the server is working an update out — the rail's quarter ring
 * breathes while it is — and, for the performance panel, when it began, its
 * number and, once done, how long it took.
 */
export function handleTickProcessingState(data: {
  is_processing: boolean;
  started_at_unix: number;
  duration_ms?: number;
  game_tick?: number;
}) {
  console.log('[FinancialsHandler] Received tick processing state:', data);
  useFinancialsStore.getState().setTickProcessingState({
    isProcessing: data.is_processing,
    startedAtUnix: data.started_at_unix,
    gameTick: data.game_tick,
    durationMs: data.duration_ms,
  });
}

/** The innovation lock as the admin's set carries it (admin.ts): every player's tree follows it. */
export function handleInnovationSpendLock(locked: boolean): void {
  useFinancialsStore.getState().setInnovationSpendLocked(locked);
}
